var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from "axios";
import { axiosPrivate } from "../../api/axios";
/**
 * Build optimistic cache data for an address add/update.
 *
 * Used by SWR's `optimisticData` to update the UI instantly before the server responds.
 *
 * - Update (addressId > 0): replaces the matching address in the array.
 * - Create (addressId === 0): appends to the array with addressId = 0 as a placeholder
 *   (the real ID is reconciled by `populateCache` after the server responds).
 */
export const buildOptimisticAddressData = (currentData, address) => {
    const baseData = currentData !== null && currentData !== void 0 ? currentData : {
        success: true,
        data: [],
        pickupLocations: [],
        usesLocation: false,
        usesZipCodes: false,
    };
    const addresses = baseData.data || [];
    const isUpdate = address.addressId > 0;
    let updatedAddresses;
    if (isUpdate) {
        const existingIndex = addresses.findIndex((addr) => addr.addressId === address.addressId);
        updatedAddresses = [...addresses];
        if (existingIndex !== -1) {
            updatedAddresses[existingIndex] = address;
        }
    }
    else {
        updatedAddresses = [...addresses, address];
    }
    return Object.assign(Object.assign({}, baseData), { success: true, data: updatedAddresses });
};
/**
 * Add or update a delivery address via POST request.
 *
 * POST /Account/AddUpdateAccountDeliveryAddress
 *
 * The backend determines create vs update based on addressId:
 * - addressId === 0: Creates a new address
 * - addressId > 0: Updates existing address
 *
 * Cache updates are handled by SWR's optimisticData and populateCache in useAddressMutations.
 * This function only makes the API call and returns the server response.
 */
export const addUpdateAddress = (key, { arg }) => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield axiosPrivate.post("/Account/AddUpdateAccountDeliveryAddress", arg.address);
    if (!response.data.success) {
        throw new Error(response.data.message || "Failed to save address");
    }
    return response.data;
});
/**
 * Process address mutation errors from axios and return a user-friendly error message.
 *
 * Handles various error formats from the backend including:
 * - Simple error messages
 * - Model validation errors
 * - Multiple error arrays
 *
 * @param error - The error object from the mutation
 * @returns A processed Error object with a user-friendly message
 */
export const processAddressError = (error) => {
    var _a;
    let message = "Something unexpected happened while saving your address.";
    if (axios.isAxiosError(error)) {
        const errorResponse = (_a = error.response) === null || _a === void 0 ? void 0 : _a.data;
        if (errorResponse === null || errorResponse === void 0 ? void 0 : errorResponse.message) {
            message = errorResponse.message;
        }
        if ((errorResponse === null || errorResponse === void 0 ? void 0 : errorResponse.errors) && Array.isArray(errorResponse.errors)) {
            // Append additional error messages
            message += ". " + errorResponse.errors.join(". ");
        }
        if (errorResponse === null || errorResponse === void 0 ? void 0 : errorResponse.modelErrors) {
            // Handle model validation errors
            const validationErrors = Object.entries(errorResponse.modelErrors)
                .flatMap(([field, errors]) => errors.map((err) => `${field}: ${err}`))
                .join(". ");
            if (validationErrors) {
                message += ". " + validationErrors;
            }
        }
    }
    return new Error(message);
};
